import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { muscleLabel } from 'components/play/constants';

const TOP = 5;

// Constante de módulo: `distribution` es dependencia del useMemo del top, y un `[]` en la firma
// se recrearía en cada render.
const DEFAULT_DISTRIBUTION = [];

/**
 * OTRIX Play — "Lo que más trabajas": top de grupos musculares por resultados registrados,
 * con barras horizontales animadas (scaleX al montar). Identifica de un vistazo dónde está
 * cayendo el trabajo real — venga de la rutina recomendada o de lo custom del cliente.
 */
function PlayTopMusclesCard({ distribution = DEFAULT_DISTRIBUTION }) {
  const { t } = useTranslation(['play']);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const top = useMemo(
    () =>
      [...(distribution || [])]
        .filter((d) => d.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, TOP),
    [distribution]
  );

  const max = Math.max(1, ...top.map((d) => d.count));

  return (
    <PremiumCard>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmojiEvents sx={{ fontSize: 18, color: 'gold.main' }} />
          <Typography variant="subtitle2" fontWeight={800}>
            {t('muscleRank.topTitle', { ns: 'play', defaultValue: 'Lo que más trabajas' })}
          </Typography>
        </Stack>

        {top.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('muscleRank.topEmpty', { ns: 'play', defaultValue: 'Cuando registres entrenamientos verás aquí tus grupos más trabajados.' })}
          </Typography>
        )}

        <Stack spacing={1.25}>
          {top.map((d, index) => (
            <Stack key={d.muscle_group} spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" fontWeight={index === 0 ? 800 : 700} sx={{ color: index === 0 ? 'gold.main' : 'text.primary' }} noWrap>
                  {index + 1}. {muscleLabel(t, d.muscle_group)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                  {t('muscleRank.topCount', { ns: 'play', count: d.count, defaultValue: '{{count}} marcas' })}
                </Typography>
              </Stack>
              <Box sx={{ height: 6, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    borderRadius: 999,
                    backgroundColor: index === 0 ? 'gold.main' : 'hairline.strong',
                    transform: `scaleX(${entered ? d.count / max : 0})`,
                    transformOrigin: 'left',
                    transition: `transform 600ms cubic-bezier(0.32, 0.72, 0, 1) ${index * 80}ms`
                  }}
                />
              </Box>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </PremiumCard>
  );
}

PlayTopMusclesCard.propTypes = {
  /** myMuscleDistribution: [{ muscle_group, count, percent }] — SOLO lectura. */
  distribution: PropTypes.arrayOf(PropTypes.object)
};

export default PlayTopMusclesCard;
